import { useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  getRememberedDriverEmail,
  isDriverSignedIn,
  setRememberedDriverEmail,
  startRiderSession,
} from '../lib/driverSession';
import { riderApi } from '../api/riders';
import { googleSignIn } from '../api/googleSignIn';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { SESSION_ENDED } from '../components/SessionListener';
import { refusalMessage } from '../api/errors';
import InGoLogo from '../components/InGoLogo';
import DriverPasswordField from '../components/driver/DriverPasswordField';
import { LOGIN_HERO_ART } from '../lib/ingoLogo';
import './driverLoginPremium.css';

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 19.5L8 12l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6Zm0 0 8 7 8-7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function DriverLoginPage({ google = googleSignIn, riders = riderApi }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [email, setEmail] = useState(() => getRememberedDriverEmail());
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Google's token, kept while the server asks for the password once before linking it.
  const [pendingGoogle, setPendingGoogle] = useState(null);

  /**
   * "Continue with Google" answered (ADR 0023 §5): a rider with an account is
   * signed in; one Google vouched for with none goes to the registration form
   * with Google's name and address and token (a rider is never made from
   * Google alone).
   */
  const googleAnswered = (result, idToken) => {
    if (result.status === 'sign_up') {
      navigate('/driver/register', { state: { google: { ...result.profile, idToken } } });
      return;
    }
    startRiderSession(result.account, { rememberMe: remember });
    navigate(result.mustChangePassword ? '/driver/choose-password' : '/driver/home', { replace: true });
  };

  const googleRefused = (error, idToken) => {
    if (error?.code === 'EMAIL_NOT_VERIFIED_HERE') setPendingGoogle(idToken);
    setErrorMessage(refusalMessage(error));
  };

  if (isDriverSignedIn()) {
    const from = state?.from;
    const target =
      typeof from === 'string' && from.startsWith('/driver') && !from.startsWith('/driver/login')
        ? from
        : '/driver/home';
    return <Navigate to={target} replace />;
  }

  /**
   * Signs the rider in on InGo's API (the mobile plan's M2). The server
   * answers a wrong password, an unknown address and a closed account with
   * one sentence, shown as it comes. A rider whose application is still open
   * signs in too: the portal's gate takes them to their application's screen.
   */
  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    const emailTrim = email.trim().toLowerCase();
    if (!emailTrim || !password) return;

    setIsSubmitting(true);
    try {
      const session = await riders.signIn(emailTrim, password);
      startRiderSession(session.account, { rememberMe: remember });
      // Google was refused until the password proved the address: link it now (ADR 0023 §5).
      if (pendingGoogle) await google.link('rider', pendingGoogle).catch(() => {});
      if (remember) setRememberedDriverEmail(emailTrim);
      else setRememberedDriverEmail('');
      // A password carried over from the old app is replaced before anything else.
      if (session.mustChangePassword) {
        // Never the password itself: navigation state lives in the browser's history.
        navigate('/driver/choose-password', { replace: true });
        return;
      }
      const from = state?.from;
      const target =
        typeof from === 'string' && from.startsWith('/driver') && !from.startsWith('/driver/login')
          ? from
          : '/driver/home';
      navigate(target, { replace: true });
    } catch (error) {
      setErrorMessage(refusalMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dp-login-page">
      <header className="dp-login-hero">
        <button
          type="button"
          className="dp-login-hero-back"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <BackIcon />
        </button>
        <div className="dp-login-hero-waves" aria-hidden />
        <div className="dp-login-hero-top">
          <InGoLogo variant="hero" />
        </div>
        <div className="dp-login-hero-scene" aria-hidden>
          <img
            src={LOGIN_HERO_ART}
            alt=""
            className="dp-login-hero-art"
            decoding="async"
          />
        </div>
      </header>

      <main className="dp-login-body">
        <div className="dp-login-card">
          <form className="dp-login-form" onSubmit={submit} autoComplete="on">
            <h1 className="dp-login-title">Welcome Back, Driver</h1>

            {state?.sessionEnded && (
              <p className="dp-login-flash" role="status">
                {SESSION_ENDED}
              </p>
            )}
            {state?.passwordReset && (
              <p className="dp-login-flash" role="status">
                Password updated. Sign in with your new password.
              </p>
            )}
            {/* "Delete my account" asked: the server's sentence names the day (ADR 0038). */}
            {typeof state?.deletionScheduled === 'string' && (
              <p className="dp-login-flash" role="status">
                {state.deletionScheduled}
              </p>
            )}
            {(state?.emailVerified || state?.pendingApproval) && (
              <p className="dp-login-flash" role="status">
                Your email is confirmed. Your account is under approval — once approved by an admin, you can log in and
                start working.
              </p>
            )}
            {state?.registered && (
              <p className="dp-login-flash" role="status">
                {state?.companyRegistered
                  ? 'Company application received. Give each biker their own email and password so they can log in after admin approval. Then open Fleet to oversee riders, orders, and earnings.'
                  : 'Application received. Wait for admin approval before you can log in and start working.'}
              </p>
            )}

            {errorMessage ? (
              <p className="dp-login-error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <div className="dp-login-field">
              <label className="dp-login-label" htmlFor="dlem">
                Email
              </label>
              <div className="dp-login-input-wrap">
                <span className="dp-login-iconbox" aria-hidden>
                  <IconEnvelope />
                </span>
                <input
                  className="dp-login-input"
                  id="dlem"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <DriverPasswordField
              id="dlpw"
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />

            <div className="dp-login-forgot-row">
              <label className="dp-login-chk">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <Link to="/forgot-password?realm=driver" className="dp-login-forgot">
                Forgot password
              </Link>
            </div>

            <button type="submit" className="dp-login-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Login'}
            </button>
          </form>

          <div className="dp-login-or" aria-hidden>
            <span className="dp-login-or-line" />
            <span className="dp-login-or-text">OR</span>
            <span className="dp-login-or-line" />
          </div>

          <GoogleSignInButton realm="rider" google={google} onResult={googleAnswered} onError={googleRefused} />

          <Link to="/driver/register" className="dp-login-register-btn">
            Register as a Driver
          </Link>
        </div>

        <p className="dp-login-customer-line">
          Are you a customer?{' '}
          <Link to="/login" className="dp-login-customer-link">
            Customer Login
          </Link>
        </p>
      </main>

      <footer className="dp-login-footer">
        <p className="dp-login-copyright">
          &copy; {new Date().getFullYear()} InGo. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
